import React from 'react';
import {
  PencilIcon,
  TrashIcon,
  UsersIcon,
  CalendarDaysIcon,
  NoSymbolIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getInitials } from '../../utils/avatarUtils';
import { formatPhoneDisplay } from '../../utils/phone';
import { getRoleDisplayName, getRoleBadgeColor } from '../../utils/roleUtils';

// ✅ BEST-OF-BEST: Optimized Employee Table
// GPT Guidance: Name, Role, Status, Schedulable, Phone, Last Active, Actions
// Hide: Email (in name cell), Joined date (use Last Active instead)
export const EmployeesTable = ({
  loading,
  employees,
  // True when a search or filter is narrowing the list. Without it the empty state told a
  // company with a full roster to "add your first team member" the moment a search matched
  // nothing (F-EMP-2026-08-31-2340-2) — which reads as data loss, not as a filter result.
  hasActiveFilters = false,
  onClearFilters,
  sortedEmployees,
  openDetailPanel,
  openEditForm,
  handleDeleteEmployee,
  handleDeactivateEmployee,
  handleReactivateEmployee,
  handleToggleSchedulable, // NEW: Toggle schedulable directly
  selectedIds,
  toggleSelectAll,
  toggleRow,
  allVisibleSelected,
  onClearSelection,
  bulkActivateCount = 0,
  bulkDeactivateCount = 0,
  onBulkActivate,
  onBulkDeactivate,
  sortBy,
  setSortBy,
  // employees.id values with time off covering today (#6154): an "Off today" badge.
  offTodayIds
}) => {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white/80 to-indigo-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-indigo-200/50 dark:border-indigo-900/50 shadow-2xl shadow-indigo-500/5 p-12">
        <div className="text-center">
          <div className="text-gray-500 dark:text-gray-400">Loading employees...</div>
        </div>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white/80 to-indigo-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-indigo-200/50 dark:border-indigo-900/50 shadow-2xl shadow-indigo-500/5 p-12">
        <div className="text-center">
          <UsersIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            {hasActiveFilters ? 'No matching team members' : 'No employees found'}
          </h3>
          {hasActiveFilters ? (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No one matches the current search or filters.
              </p>
              {onClearFilters && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
                >
                  Clear search and filters
                </button>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Add your first team member to get started</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white/80 to-indigo-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-indigo-200/50 dark:border-indigo-900/50 shadow-2xl shadow-indigo-500/5 overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-indigo-200/50 dark:border-indigo-900/50 bg-gradient-to-r from-indigo-100/60 to-indigo-50/40 dark:from-indigo-900/40 dark:to-indigo-800/20">
        <div className="flex items-center justify-between">
          {/* Board #4434: "Team Members" repeated the page H1 — this is the count label now. */}
          <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            All members · {sortedEmployees.length} employee{sortedEmployees.length !== 1 ? 's' : ''}
          </div>
          {/* #952: the bulk action bar the selection column was waiting for. Each button names
              how many of the selected rows it will actually change. */}
          {selectedIds?.size > 0 && (
            <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Bulk actions">
              <span className="text-sm text-gray-700 dark:text-gray-200">{selectedIds.size} selected</span>
              <button
                type="button"
                onClick={onBulkDeactivate}
                disabled={!bulkDeactivateCount}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
                title={bulkDeactivateCount ? '' : 'Only Active employees can be deactivated (not the owner or yourself)'}
              >
                Deactivate{bulkDeactivateCount ? ` (${bulkDeactivateCount})` : ''}
              </button>
              <button
                type="button"
                onClick={onBulkActivate}
                disabled={!bulkActivateCount}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title={bulkActivateCount ? '' : 'Only Inactive employees can be reactivated'}
              >
                Reactivate{bulkActivateCount ? ` (${bulkActivateCount})` : ''}
              </button>
              <button type="button" onClick={onClearSelection} className="px-3 py-1.5 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 hover:bg-white/60 dark:hover:bg-slate-700/60">
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-scroll">
        <table className="min-w-full divide-y divide-indigo-200/50 dark:divide-indigo-900/50">
          <thead className="bg-gradient-to-r from-indigo-100/60 to-indigo-50/40 dark:from-indigo-900/40 dark:to-indigo-800/20 backdrop-blur-sm">
            <tr>
              {/* #952: selection column, back now that the action bar above exists. */}
              <th className="pl-6 pr-2 py-4 text-left">
                <input
                  type="checkbox"
                  aria-label="Select all employees"
                  checked={!!allVisibleSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </th>
              {/* w-full on ONE column, deliberately. Every cell here holds short
                  content (a badge, a pill, a phone number), so `min-w-full` had
                  spare width and spread it evenly across all seven columns —
                  which is what put the wide gaps between them. Giving Name the
                  slack lets the rest shrink to their content and packs the row
                  the way Jobber/HCP do. */}
              <th
                className="w-full px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                onClick={() => setSortBy(s => ({ key: 'name', dir: s.key === 'name' && s.dir === 'asc' ? 'desc' : 'asc' }))}
              >
                Name {sortBy.key === 'name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                onClick={() => setSortBy(s => ({ key: 'role', dir: s.key === 'role' && s.dir === 'asc' ? 'desc' : 'asc' }))}
              >
                Role {sortBy.key === 'role' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                onClick={() => setSortBy(s => ({ key: 'status', dir: s.key === 'status' && s.dir === 'asc' ? 'desc' : 'asc' }))}
              >
                Status {sortBy.key === 'status' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Schedulable
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-indigo-200/50 dark:divide-indigo-900/50">
            {sortedEmployees.map((employee) => {
              const status = employee.status || (employee.active ? 'active' : 'inactive');
              const statusMap = {
                'active': { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-800 dark:text-green-300', label: 'Active' },
                'inactive': { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-800 dark:text-gray-300', label: 'Inactive' },
                'pending_invite': { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pending Invite' },
                // #6152: every other employee_status_enum value fell back to the green Active badge,
                // so a Terminated employee read as Active on the roster.
                'terminated': { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300', label: 'Terminated' },
                'on_leave': { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-300', label: 'On Leave' },
                'suspended': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-800 dark:text-orange-300', label: 'Suspended' },
                'probation': { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300', label: 'Probation' },
                'retired': { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-800 dark:text-gray-300', label: 'Retired' }
              };
              const statusBadge = statusMap[status] || { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-800 dark:text-gray-300', label: status };

              return (
                <tr
                  key={employee.id}
                  className="hover:bg-white/60 dark:hover:bg-slate-700/60 cursor-pointer transition-all"
                  onClick={() => openDetailPanel(employee)}
                >
                  <td className="pl-6 pr-2 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${employee.full_name || 'employee'}`}
                      checked={!!selectedIds?.has(employee.id)}
                      onChange={() => toggleRow(employee.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                          <span className="text-sm font-bold text-white">
                            {getInitials(employee.full_name)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {employee.full_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {employee.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(employee.role)}`}>
                        {employee.user_id ? getRoleDisplayName(employee.role) : 'No login'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
                      {statusBadge.label}
                    </span>
                    {offTodayIds?.has(employee.id) && (
                      <span
                        data-testid={`employee-off-today-${employee.id}`}
                        title="Has time off covering today"
                        className="ml-2 px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                      >
                        Off today
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleSchedulable && handleToggleSchedulable(employee.id, employee.is_schedulable);
                        }}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${
                          employee.is_schedulable
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:ring-green-400'
                            : 'bg-gray-100 dark:bg-gray-700/40 text-gray-600 dark:text-gray-400 hover:ring-gray-400'
                        }`}
                        title={employee.is_schedulable ? 'Click to remove from scheduling' : 'Click to make schedulable'}
                      >
                        {employee.is_schedulable ? (
                          <>
                            <CalendarDaysIcon className="w-3 h-3" />
                            Schedulable
                          </>
                        ) : (
                          <>
                            <UsersIcon className="w-3 h-3" />
                            Office Only
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {/* Display format, not the stored value: writes are E.164 but legacy rows are
                        bare digits, so the column mixed shapes (board #4288/#4425). */}
                    {(employee.phone || employee.phone_number) ? formatPhoneDisplay(employee.phone || employee.phone_number) : 'Not provided'}
                  </td>
                  {/* text-right to match the ACTIONS header above it, which was
                      already right-aligned while these buttons were not. */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEditForm(employee)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Edit employee"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Edit
                    </button>
                    {/* #1768: this tested `!== 'inactive'`, so a Pending Invite (no login, no
                        history) was offered "Deactivate — revokes login". Active only, the same
                        rule the detail panel already uses. */}
                    {(employee.status || (employee.active ? 'active' : 'inactive')) === 'active' && (
                      <button
                        onClick={() => handleDeactivateEmployee(employee.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                        title="Deactivate — revokes login, keeps their history"
                      >
                        <NoSymbolIcon className="w-4 h-4" />
                        Deactivate
                      </button>
                    )}
                    {/* Board #2679: the row offered Edit/Delete only once someone was inactive, so
                        the way back was hidden in the detail panel. Inactive only — a Pending
                        Invite never had a login to restore. */}
                    {(employee.status || (employee.active ? 'active' : 'inactive')) === 'inactive' && handleReactivateEmployee && (
                      <button
                        onClick={() => handleReactivateEmployee(employee.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        title="Reactivate — restores their login"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        Reactivate
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteEmployee(employee.id, employee.email)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 dark:text-red-400 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      title="Delete permanently — only works for someone with no work history"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
